import { useCallback, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { selectPermissions } from "store/permissions/selectors";
import { PermissionTypes } from "store/permissions/types";
import {
  resetAll,
  setIsAddNewRoleView,
  setIsDeleteModalOpen,
  setIsDescriptionModalOpen,
  setOpenedGroups,
  setRoleToEditOrDelete,
  setRoleTypeView,
  setSearchValue,
} from "store/roles/reducer";
import { selectRoles, selectRolesPage } from "store/roles/selectors";
import { Role, RoleTypes } from "store/roles/types";

export enum RoleTypesSelectValues {
  GLOBAL = "global",
  MEETINGS = "meetings",
  VOTINGS = "votings",
}

const useRolesAndPermissions = () => {
  const permissions = useAppSelector(selectPermissions);
  const dispatch = useAppDispatch();
  const { roles } = useAppSelector(selectRoles);
  const {
    openedGroups,
    roleTypeView,
    isDescriptionModalOpen,
    searchValue,
    isAddNewRoleView,
    isDeleteModalOpen,
    roleToEditOrDelete,
  } = useAppSelector(selectRolesPage);

  const globalPermissions = permissions.permissions.filter(
    (item) => item.type === PermissionTypes.GLOBAL
  );
  const meetingsPermissions = permissions.permissions.filter(
    (item) => item.type === PermissionTypes.MEETING
  );
  const votingsPermissions = permissions.permissions.filter(
    (item) => item.type === PermissionTypes.VOTING
  );

  //All global permissions groups names
  const globalPermissionsGroups = globalPermissions.length
    ? globalPermissions
        .reduce((acc, item) => {
          if (!acc.includes(item.group)) {
            acc.push(item.group);
          }
          return acc;
        }, [] as string[])
        .filter((p) => p)
    : [];

  //Permissions to show based on selected role type (global, meeting, voting) by select on roles page
  const selectedPermissions =
    roleTypeView.value === RoleTypesSelectValues.VOTINGS
      ? votingsPermissions
      : roleTypeView.value === RoleTypesSelectValues.MEETINGS
      ? meetingsPermissions
      : globalPermissions;

  //Roles sorted by id
  const sortedRoles = useMemo(() => {
    const rolesToSort = [...(roles || [])];
    return rolesToSort.sort((a, b) => a.id - b.id);
  }, [roles]);

  //Role type (for forms, for backend etc. as helper) based on selected role type (global, meeting, voting) by select on roles page
  //because values for selects are plural and values for edit single role are singular
  const roleType = useMemo(
    () =>
      roleTypeView.value === RoleTypesSelectValues.VOTINGS
        ? RoleTypes.VOTING
        : roleTypeView.value === RoleTypesSelectValues.MEETINGS
        ? RoleTypes.MEETING
        : RoleTypes.GLOBAL,
    [roleTypeView]
  );

  const resetModals = useCallback(() => {
    dispatch(setRoleToEditOrDelete(null));
    dispatch(setIsDescriptionModalOpen(false));
    dispatch(setIsDeleteModalOpen(false));
  }, [dispatch]);

  const openDeleteModal = useCallback(
    (role: Role) => {
      dispatch(setRoleToEditOrDelete(role));
      dispatch(setIsDeleteModalOpen(true));
    },
    [dispatch]
  );

  const openDescriptionModal = useCallback(
    (role: Role) => {
      dispatch(setRoleToEditOrDelete(role));
      dispatch(setIsDescriptionModalOpen(true));
    },
    [dispatch]
  );

  return {
    openedGroups,
    globalPermissionsGroups,
    globalPermissions,
    meetingsPermissions,
    votingsPermissions,
    selectedPermissions,
    sortedRoles,
    roleTypeView,
    roleType,
    setOpenedGroups,
    setRoleTypeView,
    setIsDescriptionModalOpen,
    setIsDeleteModalOpen,
    isDescriptionModalOpen,
    setRoleToEditOrDelete,
    setSearchValue,
    searchValue,
    setIsAddNewRoleView,
    isAddNewRoleView,
    isDeleteModalOpen,
    roleToEditOrDelete,
    resetAll,
    resetModals,
    openDeleteModal,
    openDescriptionModal,
  };
};

export default useRolesAndPermissions;
